import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Card, Icon } from '../ui'

/**
 * The first-run guide: three steps, and what the app will show afterwards.
 *
 * ── THE PROCESS, NEVER A MEASUREMENT ────────────────────────────────────────
 *
 * Every sentence here describes what the person does and what the app does with
 * it. None describes a reading. The canvas's "50 means 'at your usual'" and
 * "Six measures" are struck (O2, R30): six of the eight slots are scaled so 50
 * is the undisturbed value and two are not, the slot count is the payload's,
 * and a client sentence about either would be a client-authored scientific
 * claim. So would "a few minutes after", which is a timing nothing here knows.
 *
 * Where the app already ships a sentence for one of these facts, that sentence
 * is reused verbatim rather than rewritten — the separate-baselines rule and
 * the same-day rule are Baseline's own words, and the journal's are the
 * journal's.
 *
 * ── AND ONE WAY FORWARD ─────────────────────────────────────────────────────
 *
 * Exactly one `Record a session` link on the screen, and it is here rather than
 * in the page header: on first run this card IS the screen, and two identical
 * links to the same place is a choice presented where there is none.
 *
 * The trust line that used to close this guide is now Home's own (R38), and
 * renders in every state rather than on first run alone.
 */
export function FirstRunGuide({ action }: { action?: ReactNode } = {}) {
  return (
    <div className="home-pair">
      <Card active eyebrow="Start here">
        <ol className="guide-steps">
          <li>
            <span>
              <strong>Put on your headband</strong> and open Record in Chrome. Every contact is shown on a
              head map, and the page says which one to adjust.
            </span>
          </li>
          <li>
            <span>
              <strong>Record three quiet minutes</strong> with your eyes closed. When it ends, the recording is
              checked and scored by itself — there is no file to save and nothing to hand over.
            </span>
          </li>
          <li>
            <span>
              <strong>Come back to Report.</strong> Your first Daily Status appears there once the
              recording has been scored.
            </span>
          </li>
        </ol>
        {/* Sprint 1.9.9 D6: `null` under the mirror, whose top section carries
            the one Record action; absent, the guide's own link as before. */}
        {action !== null && (
          <div className="guide-actions">
            {action ?? (
              <Link className="btn btn--primary" to="/record">
                <Icon name="record" />
                Record a session
              </Link>
            )}
          </div>
        )}
      </Card>

      <Card eyebrow="What you will see" className="home-guide__secondary">
        <ul className="guide-list">
          <li>
            <span>Report shows your Daily Status once a session has been recorded and scored.</span>
          </li>
          <li>
            <span>
              A baseline is built from your own scored recordings for one condition. Eyes
              closed and eyes open are separate baselines and are never combined.
            </span>
          </li>
          <li>
            <span>Recording twice in one day counts as one baseline day.</span>
          </li>
          <li>
            <span>
              A journal for each recording. Nothing in it is required, nothing is
              interpreted, and it never changes your result.
            </span>
          </li>
        </ul>
      </Card>
    </div>
  )
}
